<template>
  <div class="product-variants">
    <!-- Section pour activer/configurer les variantes -->
    <div class="border rounded-lg p-4 mb-4">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-700">Variantes du produit</h3>
        <label class="toggle-switch flex items-center cursor-pointer">
          <span class="mr-3 text-sm text-gray-600">Activer les variantes</span>
          <div class="toggle-container">
            <input type="checkbox" v-model="hasVariants" @change="toggleVariants" class="toggle-input" />
            <span class="toggle-track" :class="{'active': hasVariants}">
              <span class="toggle-thumb" :class="{'active': hasVariants}"></span>
            </span>
          </div>
        </label>
      </div>

      <template v-if="hasVariants">
        <!-- Sélection des attributs -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Attributs variables</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="attr in availableAttributes"
              :key="attr.code"
              @click="toggleAttribute(attr)"
              :class="isAttributeSelected(attr.code) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'"
              class="px-3 py-1 rounded-full text-sm font-medium transition-colors"
            >
              {{ attr.name }}
            </button>
          </div>
        </div>

        <!-- Configuration des valeurs pour chaque attribut sélectionné -->
        <div v-if="selectedAttributes.length > 0" class="space-y-4">
          <div v-for="attr in selectedAttributes" :key="attr.code" class="border rounded p-3 bg-gray-50">
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium text-gray-700">{{ attr.name }}</span>
              <button @click="removeAttribute(attr.code)" class="text-red-500 hover:text-red-700">
                <i class="material-icons text-sm">close</i>
              </button>
            </div>

            <!-- Valeurs prédéfinies -->
            <div class="flex flex-wrap gap-2 mb-2">
              <button
                v-for="val in attr.values"
                :key="val.value"
                @click="toggleAttributeValue(attr.code, val)"
                :class="isValueSelected(attr.code, val.value) ? 'bg-blue-500 text-white' : 'bg-white border border-gray-300'"
                class="px-2 py-1 rounded text-sm transition-colors"
              >
                <span v-if="attr.type === 'color' && val.colorCode"
                      class="inline-block w-4 h-4 rounded-full mr-1"
                      :style="{backgroundColor: val.colorCode}"></span>
                {{ val.label || val.value }}
              </button>
            </div>

            <!-- Ajouter une nouvelle valeur -->
            <div class="flex items-center gap-2 mt-2">
              <input
                v-model="newValues[attr.code]"
                @keyup.enter="addCustomValue(attr.code)"
                type="text"
                :placeholder="`Ajouter ${attr.name.toLowerCase()}...`"
                class="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:border-blue-500"
              />
              <button @click="addCustomValue(attr.code)" class="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
                +
              </button>
            </div>
          </div>

          <!-- Bouton pour générer les variantes -->
          <div class="pt-4 border-t">
            <button
              @click="generateVariants"
              :disabled="!canGenerateVariants"
              class="w-full py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Générer {{ variantCount }} variante(s)
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Liste des variantes existantes -->
    <div v-if="variants.length > 0" class="border rounded-lg overflow-hidden">
      <div class="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
        <h4 class="font-semibold text-gray-700">Variantes ({{ variants.length }})</h4>
        <div class="flex items-center space-x-2">
          <!-- Bouton modifier tous les prix -->
          <button @click="openBulkPriceModal" class="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center">
            <i class="material-icons text-sm mr-1">edit</i>
            Modifier tous les prix
          </button>
          <!-- Bouton supprimer toutes les variantes -->
          <button @click="deleteAllVariants" class="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 flex items-center">
            <i class="material-icons text-sm mr-1">delete_sweep</i>
            Supprimer tout
          </button>
        </div>
      </div>

      <!-- Barre de modification prix en masse -->
      <div v-if="showBulkPriceEdit" class="bg-blue-50 px-4 py-3 border-b flex items-center space-x-3">
        <span class="text-sm text-gray-700">Nouveau prix pour toutes les variantes:</span>
        <input
          v-model.number="bulkPrice"
          type="number"
          min="0"
          placeholder="Prix..."
          class="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:border-blue-500"
        />
        <span class="text-xs text-gray-500">{{ currency }}</span>
        <button @click="applyBulkPrice" class="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600">
          Appliquer
        </button>
        <button @click="showBulkPriceEdit = false" class="px-3 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500">
          Annuler
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variante</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="variant in variants" :key="variant.id" :class="{'bg-red-50': !variant.isActive}">
              <td class="px-4 py-3">
                <div class="flex items-center">
                  <img v-if="variant.picture" :src="variant.picture" class="w-10 h-10 rounded mr-2 object-cover" />
                  <span class="text-sm text-gray-900">{{ formatVariantLabel(variant.attributes) }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span class="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">{{ variant.sku || '-' }}</span>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center">
                  <input
                    v-model.number="variant.price"
                    @blur="updateVariant(variant)"
                    type="number"
                    min="0"
                    class="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:border-blue-500"
                  />
                  <span class="ml-1 text-xs text-gray-500">{{ currency }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-col text-sm">
                  <span class="text-green-600">En stock: {{ variant.quantity.inStock }}</span>
                  <span class="text-gray-500">Total: {{ variant.quantity.total }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center space-x-2">
                  <button @click="openStockModal(variant)" class="p-1 text-blue-500 hover:text-blue-700" title="Gérer le stock">
                    <i class="material-icons text-sm">inventory</i>
                  </button>
                  <button @click="toggleVariantActive(variant)" :class="variant.isActive ? 'text-yellow-500' : 'text-green-500'" title="Activer/Désactiver">
                    <i class="material-icons text-sm">{{ variant.isActive ? 'visibility_off' : 'visibility' }}</i>
                  </button>
                  <button @click="deleteVariant(variant)" class="p-1 text-red-500 hover:text-red-700" title="Supprimer">
                    <i class="material-icons text-sm">delete</i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de gestion du stock par variante -->
    <vue-modal-2 name="stockModal" :headerOptions="{title: 'Gestion du stock - ' + (selectedVariant ? formatVariantLabel(selectedVariant.attributes) : '')}"
                 :noFooter="true" modalSize="md" @on-close="closeStockModal">
      <div v-if="selectedVariant" class="p-4">
        <!-- Stock par entrepôt -->
        <div class="space-y-4">
          <div v-for="(ws, index) in selectedVariant.warehouseStocks" :key="index" class="border rounded p-3">
            <div class="flex items-center justify-between mb-2">
              <span class="font-medium">
                <img v-if="ws.warehouse && ws.warehouse.flag" :src="ws.warehouse.flag" class="inline w-5 h-4 mr-1" />
                {{ ws.warehouse ? ws.warehouse.name : ws.country }}
              </span>
            </div>

            <div class="grid grid-cols-3 gap-3 text-sm">
              <div>
                <label class="block text-gray-500 mb-1">En stock</label>
                <span class="text-green-600 font-semibold">{{ ws.quantity.inStock }}</span>
              </div>
              <div>
                <label class="block text-gray-500 mb-1">Total</label>
                <span class="font-semibold">{{ ws.quantity.total }}</span>
              </div>
              <div>
                <label class="block text-gray-500 mb-1">Défectueux</label>
                <span class="text-red-600 font-semibold">{{ ws.quantity.defective }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Ajuster le stock -->
        <div class="mt-4 pt-4 border-t">
          <h5 class="font-medium mb-3">Ajuster le stock</h5>
          <div class="space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm text-gray-600 mb-1">Entrepôt</label>
                <v-select v-model="stockAdjustment.warehouse" :options="warehouses" label="name" placeholder="Sélectionner..."></v-select>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">Type de stock</label>
                <select v-model="stockAdjustment.stockType" class="w-full px-2 py-2 border rounded focus:outline-none focus:border-blue-500">
                  <option value="inStock-total">En stock + Total</option>
                  <option value="inStock">En stock uniquement</option>
                  <option value="total">Total uniquement</option>
                  <option value="defective">Défectueux</option>
                </select>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm text-gray-600 mb-1">Opération</label>
                <select v-model="stockAdjustment.operation" class="w-full px-2 py-2 border rounded focus:outline-none focus:border-blue-500">
                  <option value="Addition">Ajouter</option>
                  <option value="Subtraction">Retirer</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-gray-600 mb-1">Quantité</label>
                <input v-model.number="stockAdjustment.quantity" type="number" min="1" class="w-full px-2 py-2 border rounded focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div>
              <label class="block text-sm text-gray-600 mb-1">Raison</label>
              <input v-model="stockAdjustment.reason" type="text" placeholder="Raison de l'ajustement..." class="w-full px-2 py-2 border rounded focus:outline-none focus:border-blue-500" />
            </div>
            <button
              @click="adjustStock"
              :disabled="!canAdjustStock"
              class="w-full py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Appliquer l'ajustement
            </button>
          </div>
        </div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
export default {
  name: 'ProductVariants',
  props: {
    productId: { type: String, default: null },
    initialVariants: { type: Array, default: () => [] },
    initialHasVariants: { type: Boolean, default: false },
    warehouses: { type: Array, default: () => [] },
    currency: { type: String, default: 'MAD' }
  },
  data() {
    return {
      hasVariants: false,
      availableAttributes: [],
      selectedAttributes: [],
      selectedValues: {}, // { attributeCode: [values] }
      newValues: {}, // Pour ajouter des valeurs personnalisées
      variants: [],
      selectedVariant: null,
      stockAdjustment: {
        warehouse: null,
        stockType: 'inStock-total',
        operation: 'Addition',
        quantity: 1,
        reason: ''
      },
      loading: false,
      // Pour la modification en masse des prix
      showBulkPriceEdit: false,
      bulkPrice: 0
    }
  },
  computed: {
    canGenerateVariants() {
      return this.selectedAttributes.length > 0 &&
             this.selectedAttributes.every(attr =>
               this.selectedValues[attr.code] && this.selectedValues[attr.code].length > 0
             );
    },
    variantCount() {
      if (!this.canGenerateVariants) return 0;
      return this.selectedAttributes.reduce((acc, attr) => {
        const valCount = this.selectedValues[attr.code] ? this.selectedValues[attr.code].length : 0;
        return acc * (valCount || 1);
      }, 1);
    },
    canAdjustStock() {
      return this.stockAdjustment.warehouse &&
             this.stockAdjustment.quantity > 0 &&
             this.stockAdjustment.reason;
    }
  },
  async mounted() {
    await this.loadAttributes();
    this.hasVariants = this.initialHasVariants;
    this.variants = [...this.initialVariants];
  },
  watch: {
    initialVariants: {
      handler(newVal) {
        this.variants = [...newVal];
      },
      deep: true
    },
    initialHasVariants(newVal) {
      this.hasVariants = newVal;
    }
  },
  methods: {
    async loadAttributes() {
      // Attributs par défaut
      const defaultAttributes = [
        { code: 'size', name: 'Taille / Pointure', type: 'select', values: [
          { value: '36', label: '36' }, { value: '37', label: '37' }, { value: '38', label: '38' },
          { value: '39', label: '39' }, { value: '40', label: '40' }, { value: '41', label: '41' },
          { value: '42', label: '42' }, { value: '43', label: '43' }, { value: '44', label: '44' },
          { value: 'S', label: 'S' }, { value: 'M', label: 'M' }, { value: 'L', label: 'L' },
          { value: 'XL', label: 'XL' }, { value: 'XXL', label: 'XXL' }
        ]},
        { code: 'color', name: 'Couleur', type: 'color', values: [
          { value: 'Noir', label: 'Noir', colorCode: '#000000' },
          { value: 'Blanc', label: 'Blanc', colorCode: '#FFFFFF' },
          { value: 'Rouge', label: 'Rouge', colorCode: '#FF0000' },
          { value: 'Bleu', label: 'Bleu', colorCode: '#0000FF' },
          { value: 'Vert', label: 'Vert', colorCode: '#008000' },
          { value: 'Gris', label: 'Gris', colorCode: '#808080' }
        ]},
        { code: 'model', name: 'Modèle', type: 'select', values: [
          { value: 'Air Max', label: 'Air Max' },
          { value: 'Air Force', label: 'Air Force' },
          { value: 'Jordan', label: 'Jordan' }
        ]}
      ];

      try {
        // Utilise la nouvelle route: products/searchAttributes (anciennement productAttributes/search)
        const res = await this.$server.searchAttributes('products', { isActive: true, isDeleted: null });
        if (res && res.content && res.content.results && res.content.results.length > 0) {
          this.availableAttributes = res.content.results;
        } else {
          // Utiliser les attributs par défaut si aucun résultat
          this.availableAttributes = defaultAttributes;
        }
      } catch (error) {
        console.error('Error loading attributes:', error);
        // Attributs par défaut si l'API ne répond pas
        this.availableAttributes = defaultAttributes;
      }
    },
    toggleVariants() {
      this.$emit('update:hasVariants', this.hasVariants);
      if (!this.hasVariants) {
        this.selectedAttributes = [];
        this.selectedValues = {};
      }
    },
    isAttributeSelected(code) {
      return this.selectedAttributes.some(a => a.code === code);
    },
    toggleAttribute(attr) {
      const index = this.selectedAttributes.findIndex(a => a.code === attr.code);
      if (index === -1) {
        this.selectedAttributes.push(attr);
        this.$set(this.selectedValues, attr.code, []);
        this.$set(this.newValues, attr.code, '');
      } else {
        this.selectedAttributes.splice(index, 1);
        this.$delete(this.selectedValues, attr.code);
        this.$delete(this.newValues, attr.code);
      }
    },
    removeAttribute(code) {
      const index = this.selectedAttributes.findIndex(a => a.code === code);
      if (index !== -1) {
        this.selectedAttributes.splice(index, 1);
        this.$delete(this.selectedValues, code);
        this.$delete(this.newValues, code);
      }
    },
    isValueSelected(attrCode, value) {
      return this.selectedValues[attrCode] && this.selectedValues[attrCode].some(v => v.value === value);
    },
    toggleAttributeValue(attrCode, val) {
      if (!this.selectedValues[attrCode]) {
        this.$set(this.selectedValues, attrCode, []);
      }
      const index = this.selectedValues[attrCode].findIndex(v => v.value === val.value);
      if (index === -1) {
        this.selectedValues[attrCode].push(val);
      } else {
        this.selectedValues[attrCode].splice(index, 1);
      }
    },
    addCustomValue(attrCode) {
      const value = this.newValues[attrCode];
      if (!value || !value.trim()) return;

      const attr = this.selectedAttributes.find(a => a.code === attrCode);
      if (!attr) return;

      // Ajouter à la liste des valeurs de l'attribut
      const newVal = { value: value.trim(), label: value.trim() };
      if (!attr.values.some(v => v.value === newVal.value)) {
        attr.values.push(newVal);
      }

      // Sélectionner automatiquement la nouvelle valeur
      if (!this.selectedValues[attrCode]) {
        this.$set(this.selectedValues, attrCode, []);
      }
      if (!this.selectedValues[attrCode].some(v => v.value === newVal.value)) {
        this.selectedValues[attrCode].push(newVal);
      }

      this.newValues[attrCode] = '';
    },
    async generateVariants() {
      if (!this.productId) {
        this.$toast.warning('Veuillez d\'abord enregistrer le produit');
        return;
      }

      if (!this.canGenerateVariants) return;

      this.loading = true;
      try {
        const attributeGroups = this.selectedAttributes.map(attr => ({
          code: attr.code,
          name: attr.name,
          values: this.selectedValues[attr.code].map(v => v.value)
        }));

        const res = await this.$server.update('products', {
          _id: this.productId,
          generateVariants: true,
          attributeGroups: attributeGroups
        });

        if (res && res.content) {
          this.$toast.success(`${res.content.created} variante(s) créée(s)`);
          this.$emit('variants-generated', res.content.variants);
          // Recharger les variantes
          await this.loadVariants();
        }
      } catch (error) {
        console.error('Error generating variants:', error);
        this.$toast.error('Erreur lors de la génération des variantes');
      } finally {
        this.loading = false;
      }
    },
    async loadVariants() {
      if (!this.productId) return;
      try {
        const res = await this.$server.get('products', { _id: this.productId, getVariantStock: true });
        if (res.content && res.content.variants) {
          this.variants = res.content.variants;
        }
      } catch (error) {
        console.error('Error loading variants:', error);
      }
    },
    formatVariantLabel(attributes) {
      if (!attributes || attributes.length === 0) return '';
      return attributes.map(attr => `${attr.attributeName || attr.attributeCode}: ${attr.label || attr.value}`).join(', ');
    },
    async updateVariant(variant) {
      if (!this.productId) return;
      try {
        await this.$server.update('products', {
          _id: this.productId,
          updateVariant: true,
          variantId: variant.id,
          sku: variant.sku,
          price: variant.price,
          isActive: variant.isActive
        });
      } catch (error) {
        console.error('Error updating variant:', error);
        this.$toast.error('Erreur lors de la mise à jour');
      }
    },
    async toggleVariantActive(variant) {
      variant.isActive = !variant.isActive;
      await this.updateVariant(variant);
    },
    async deleteVariant(variant) {
      if (variant.quantity.inStock > 0 || variant.quantity.total > 0) {
        this.$toast.warning('Impossible de supprimer une variante avec du stock');
        return;
      }

      const confirmed = await this.$confirm('Voulez-vous vraiment supprimer cette variante ?');
      if (!confirmed) return;

      try {
        await this.$server.update('products', {
          _id: this.productId,
          deleteVariant: true,
          variantId: variant.id
        });

        const index = this.variants.findIndex(v => v.id === variant.id);
        if (index !== -1) {
          this.variants.splice(index, 1);
        }
        this.$toast.success('Variante supprimée');
      } catch (error) {
        console.error('Error deleting variant:', error);
        this.$toast.error('Erreur lors de la suppression');
      }
    },
    // Supprimer toutes les variantes (local uniquement, sauvegardé au Save)
    async deleteAllVariants() {
      if (this.variants.length === 0) return;

      // Vérifier si des variantes ont du stock
      const variantsWithStock = this.variants.filter(v => v.quantity.inStock > 0 || v.quantity.total > 0);
      if (variantsWithStock.length > 0) {
        this.$toast.warning(`${variantsWithStock.length} variante(s) ont du stock. Veuillez d'abord vider le stock.`);
        return;
      }

      const confirmed = await this.$confirm(`Voulez-vous vraiment supprimer toutes les ${this.variants.length} variantes ?`);
      if (!confirmed) return;

      // Supprimer localement uniquement
      this.variants = [];
      this.$toast.success('Toutes les variantes ont été supprimées (non sauvegardé)');

      // Émettre les changements au parent pour sauvegarde ultérieure
      this.$emit('variants-updated', this.variants);
      this.$emit('variants-deleted');
    },
    // Ouvrir la barre de modification des prix en masse
    openBulkPriceModal() {
      this.showBulkPriceEdit = true;
      this.bulkPrice = this.variants.length > 0 ? this.variants[0].price : 0;
    },
    // Appliquer le prix à toutes les variantes (local uniquement, sauvegardé au Save)
    applyBulkPrice() {
      if (this.bulkPrice < 0) {
        this.$toast.warning('Le prix doit être positif');
        return;
      }

      // Mettre à jour localement uniquement
      this.variants.forEach(v => v.price = this.bulkPrice);
      this.showBulkPriceEdit = false;
      this.$toast.success(`Prix mis à jour pour ${this.variants.length} variantes (non sauvegardé)`);

      // Émettre les changements au parent pour sauvegarde ultérieure
      this.$emit('variants-updated', this.variants);
    },
    openStockModal(variant) {
      this.selectedVariant = variant;
      this.stockAdjustment = {
        warehouse: null,
        stockType: 'inStock-total',
        operation: 'Addition',
        quantity: 1,
        reason: ''
      };
      this.$vm2.open('stockModal');
    },
    closeStockModal() {
      this.$vm2.close('stockModal');
      this.selectedVariant = null;
    },
    async adjustStock() {
      if (!this.canAdjustStock || !this.selectedVariant) return;

      this.loading = true;
      try {
        const res = await this.$server.update('products', {
          _id: this.productId,
          updateVariantStock: true,
          variantId: this.selectedVariant.id,
          warehouse: this.stockAdjustment.warehouse,
          country: this.stockAdjustment.warehouse.country,
          stockType: this.stockAdjustment.stockType,
          operation: this.stockAdjustment.operation,
          quantity: this.stockAdjustment.quantity,
          reason: this.stockAdjustment.reason
        });

        if (res && res.content) {
          this.$toast.success('Stock ajusté avec succès');
          await this.loadVariants();
          this.closeStockModal();
        }
      } catch (error) {
        console.error('Error adjusting stock:', error);
        this.$toast.error('Erreur lors de l\'ajustement du stock');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
/* Toggle Switch Styles */
.toggle-container {
  position: relative;
  display: inline-block;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  display: block;
  width: 44px;
  height: 24px;
  background-color: #d1d5db;
  border-radius: 12px;
  position: relative;
  transition: background-color 0.3s ease;
}

.toggle-track.active {
  background-color: #22c55e;
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
}

.toggle-thumb.active {
  transform: translateX(20px);
}
</style>
